import React, { useEffect, useState } from "react";
import { getAllPaginatedNews } from "../../../src/API/News/newsService";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../src/components/ui/pagination";
import { Button } from "../../../src/components/ui/button";
import { CiSearch } from "react-icons/ci";
import { Input } from "../../../src/components/ui/input";

interface NewsItem {
  title: string;
  date: string;
  description: string;
  image: string;
  linkUrl: string;
  slug: string;
}

interface NewsCardProps {
  title: string;
  date: string;
  description: string;
  image: string;
  linkUrl: string;
  slug: string;
}

const NewsCard: React.FC<NewsCardProps> = ({
  title,
  date,
  description,
  image,
  slug,
}) => {
  const monthAbbreviations: { [key: number]: string } = {
    1: "janv.",
    2: "fév.",
    3: "mars",
    4: "avr.",
    5: "mai",
    6: "juin",
    7: "juil.",
    8: "août",
    9: "sept.",
    10: "oct.",
    11: "nov.",
    12: "déc.",
  };

  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD || "";

  
  //Sécurisation du split
  let year = "", month = "", day = "";
  if (typeof date === "string" && date.includes("-")) {
    [year, month, day] = date.split("-");
  }

  return (
    <div className="flex flex-col w-full max-w-sm m-4 bg-white rounded-lg shadow-lg md:w-80">
      <img
        className="object-cover w-full h-48"
        src={`${uploadUrl}/news/${image}`}
        alt={title}
      />
      <div className="flex-grow px-6 py-4">
        <div className="mb-2 font-bold line-clamp-2">{title}</div>
        <div
          className="w-full text-sm line-clamp-3"
          dangerouslySetInnerHTML={{
            __html:
              description.substring(0, 80).replace(/<\/?strong>/g, "") +
              (description.length > 80 ? "..." : ""),
          }}
        />
      </div>
      <div className="flex items-center justify-between px-6 py-4">
        <a
          href={`/actualités/${slug}`}
          className="text-sm text-primary hover:text-blue-700"
        >
          Lire plus
        </a>

        {/* Affichage de la date formatée */}
        <div className="flex items-center space-x-1 text-sm text-gray-500">
          <div className="text-sm text-primary">{day}</div>
          <div className="text-sm text-primary">
            {monthAbbreviations[parseInt(month)]}
          </div>
          <div className="text-sm text-primary">{year}</div>
        </div>

      </div>
    </div>
  );
};


const AllNews: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
    const [keyword, setKeyword] = useState("");
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchData = async () => {
    try {
      const response = await getAllPaginatedNews(page,keyword);
      if (response) {
        setNews(response.news || []);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error("Failed to fetch news:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const handleNextPageChange = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePreviousPageChange = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    setPage(1);  
  };

  const handleSearchClick = () => {
    setPage(1);  
    fetchData();
  };
  return (
    <div className="container p-8 mx-auto">
      <h1 className="mb-4 text-2xl font-bold text-center text-black">
        Actualités récentes
      </h1>
      <h2 className="text-center">
        Retrouvez ici les dernières informations, événements et projets en
        cours.
      </h2>
      <div className="relative mx-auto w-[600px]">
        {/* Wrapper for the input, icon, and button */}
        <div className="relative flex items-center mt-8">
          {/* Search Icon */}
          <div className="absolute pointer-events-none left-3">
            <CiSearch className="w-5 h-5 text-black" />
          </div>

          {/* Search Input */}
          <Input
            className="py-3 pl-12 pr-12 bg-white border border-primary text-primary h-9 rounded-3xl focus:outline-none focus:border-primary"
            placeholder="Rechercher une actualité..."
             value={keyword}
             onChange={handleInputChange}
          />

          {/* Search Button */}
          <Button
            className="absolute top-0 bottom-0 right-0 px-6 py-3 text-white rounded-r-md bg-primary rounded-3xl"
            onClick={handleSearchClick}
          >
            Rechercher
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 mt-8 md:grid-cols-3">
        {news.map((item, index) => (
          <NewsCard
            key={index}
            title={item.title}
            date={item.date}
            description={item.description}
            image={item.image}
            linkUrl={item.linkUrl}
            slug={item.slug}
          />
        ))}

      </div>
      <div className="flex justify-center">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                className="cursor-pointer hover:bg-transparent"
                onClick={handlePreviousPageChange}
              />
            </PaginationItem>
            {[...Array(totalPages)].map((_, index) => (
              <PaginationItem key={index}>
                <PaginationLink
                  className="cursor-pointer hover:bg-transparent"
                  onClick={() => setPage(index + 1)}
                >
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-10 h-10 text-white rounded-full bg-primary "
                  >
                    {index + 1}
                  </Button>
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={handleNextPageChange}
                className="cursor-pointer hover:bg-transparent text-primary"
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};

export default AllNews;
